import {PublicClientApplication, AccountInfo, AuthenticationResult, InteractionRequiredAuthError} from "@azure/msal-browser";
import {backofficeApiRequest, loginRequest, msalConfig} from "../configuration/authConfig";

/**
 * Singleton MSAL PublicClientApplication instance used across the app.
 */
export const msalInstance = new PublicClientApplication(msalConfig);

export class ApiInteractionRequiredError extends Error {
  constructor() {
    super("The user must interactively sign in again before an API token can be issued.");
    this.name = "ApiInteractionRequiredError";
  }
}

export const API_INTERACTION_REQUIRED_EVENT = "outletstocks:api-interaction-required";

export const notifyApiInteractionRequired = () => {
  window.dispatchEvent(new Event(API_INTERACTION_REQUIRED_EVENT));
};

/**
 * Returns the first signed-in account (if any).
 */
export const getActiveAccount = (): AccountInfo | null => {
  const current = msalInstance.getActiveAccount();
  if (current) return current;
  const all = msalInstance.getAllAccounts();
  return all.length > 0 ? all[0] : null;
};

/**
 * Attempt to silently acquire a valid access token for the given scopes.
 * If silent acquisition fails with an InteractionRequiredAuthError, the caller
 * can decide to trigger an interactive flow (redirect/popup).
 */
export const acquireTokenSilent = async (forceRefresh = false): Promise<string> => {
  const account = getActiveAccount();
  if (!account) {
    throw new ApiInteractionRequiredError();
  }

  try {
    const result: AuthenticationResult = await msalInstance.acquireTokenSilent({
      ...backofficeApiRequest,
      account,
      forceRefresh,
    });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      throw new ApiInteractionRequiredError();
    }
    throw err;
  }
};

/**
 * Simple JWT expiry check.
 */
export const isTokenExpired = (token?: string | null): boolean => {
  if (!token) return true;
  try {
    const [, payloadBase64] = token.split(".");
    const payload = JSON.parse(atob(payloadBase64));
    const nowSec = Date.now() / 1000;
    return payload.exp < nowSec;
  } catch (_e) {
    return true;
  }
};

export const loginRedirect = () => msalInstance.loginRedirect(loginRequest);
export const logoutRedirect = () => msalInstance.logoutRedirect();
